import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Award, ArrowLeft, ArrowRight, ArrowUpRight, BadgeCheck, BellRing, Building2, Calendar, Check, ChevronLeft, ClipboardList,
  Clock, FileCheck, FileStack, FileText, Globe, HeartHandshake, IndianRupee, ListChecks, RefreshCw, ShieldCheck, Sparkles,
  TrendingUp, ChevronRight, User, UserCheck, Users, Zap, Phone, UploadCloud,
} from "lucide-react";
import { Icon } from "@/components/landing/Icon";
import { UploadIcon3D } from "@/components/landing/UploadIcon3D";
import { AmbientOrbs } from "@/components/landing/Ambient";
import { FlowDiagram } from "@/components/landing/FlowDiagram";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { PHONE } from "@/lib/data";
import SEO from "@/components/SEO";
import { getServiceSchema, getFAQPageSchema } from "@/lib/seoSchemas";

// Sticky sub-nav pinned just below the fixed navbar (68px, matches LatestUpdates.jsx).
// Sections stay mounted in one scroll flow; the active pill tracks scroll
// position instead of only reacting to clicks (see useScrollSpy below).
const NAV_OFFSET = 68;
const TAB_BAR_HEIGHT = 60;
const SCROLL_OFFSET = NAV_OFFSET + TAB_BAR_HEIGHT;

const SERVICE_SECTIONS = [
  { id: "eligibility", label: "Eligibility" },
  { id: "documents", label: "Documents" },
  { id: "process", label: "Process" },
  { id: "timeline", label: "Timeline" },
  { id: "fees", label: "Fees" },
  { id: "benefits", label: "Benefits" },
  { id: "faqs", label: "FAQs" },
];

const useScrollSpy = (ids) => {
  const [active, setActive] = useState(ids[0]);
  useEffect(() => {
    const onScroll = () => {
      let current = ids[0];
      for (const id of ids) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top - SCROLL_OFFSET <= 0) current = id;
      }
      setActive(current);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return active;
};

const scrollToSection = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  const top = el.getBoundingClientRect().top + window.scrollY - SCROLL_OFFSET + 1;
  window.scrollTo({ top, behavior: "smooth" });
};

export const ACCENT = {
  emerald: {
    text: "text-emerald2-600", bg: "bg-emerald2-500/10", border: "border-emerald2-300/40",
    solidBg: "bg-emerald2-500", chip: "bg-emerald2-500/[0.06] border-emerald2-300/40", hex: "#10b981",
  },
  gold: {
    text: "text-gold-700", bg: "bg-gold-500/10", border: "border-gold-300/50",
    solidBg: "bg-gold-500", chip: "bg-gold-500/[0.06] border-gold-300/50", hex: "#d4a24c",
  },
};

// One consistent card shell; `span` sets how many of the 4 base columns it
// claims so the grid below reads as a bento dashboard, not a linear scroll.
const Card = ({ span = "lg:col-span-2", className = "", children, ...rest }) => (
  <div className={`rounded-3xl border border-slate-200 bg-white p-6 md:p-8 ${span} ${className}`} {...rest}>
    {children}
  </div>
);

// ponytail: items are free-text, not typed — cycle icon+color pairs rather than
// keyword-matching every string. Every InfoList-driven section (eligibility,
// benefits...) used to render with one repeated icon; this gives each row its
// own glyph and color so the section doesn't read as a templated checklist.
const ICON_COLORS = [
  { bg: "bg-blue-500/10", text: "text-blue-600" },
  { bg: "bg-emerald-500/10", text: "text-emerald-600" },
  { bg: "bg-amber-500/10", text: "text-amber-600" },
  { bg: "bg-rose-500/10", text: "text-rose-600" },
  { bg: "bg-violet-500/10", text: "text-violet-600" },
  { bg: "bg-cyan-500/10", text: "text-cyan-600" },
];

const InfoList = ({ items, icons = [Check] }) => (
  <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
    {items.map((item, i) => {
      const ItemIcon = icons[i % icons.length];
      const color = ICON_COLORS[i % ICON_COLORS.length];
      return (
        <motion.div
          key={item}
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: false, margin: "-60px" }}
          transition={{ duration: 0.35, delay: (i % 6) * 0.05 }}
          whileHover={{ y: -3 }}
          className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-[0_2px_10px_rgba(15,30,60,0.04)] transition-shadow duration-300 hover:shadow-[0_14px_28px_rgba(15,30,60,0.09)]"
        >
          <motion.span
            initial={{ scale: 0.7, rotate: -8 }}
            whileInView={{ scale: 1, rotate: 0 }}
            viewport={{ once: false, margin: "-60px" }}
            transition={{ duration: 0.4, delay: (i % 6) * 0.05 }}
            className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ${color.bg} ${color.text}`}
          >
            <ItemIcon className="h-5 w-5" />
          </motion.span>
          <p className="pt-1.5 text-sm leading-relaxed text-navy-800">{item}</p>
        </motion.div>
      );
    })}
  </div>
);

// ponytail: doc names are free-text, not typed — cycle a fixed palette rather than mapping icons per keyword
const DOC_ICON_COLORS = ["text-blue-500", "text-emerald-500", "text-amber-500", "text-rose-500", "text-violet-500", "text-cyan-600"];

const DocumentGroups = ({ groups }) => (
  <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200">
    <table className="w-full text-left text-sm">
      <thead>
        <tr className="bg-[rgb(238,242,250)] text-xs font-bold uppercase tracking-wider text-slate-500">
          <th className="w-40 whitespace-nowrap px-4 py-3">Category</th>
          <th className="px-4 py-3">Document</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {groups.flatMap((g) =>
          g.items.map((item, i) => (
            <tr key={`${g.title}-${item}`} className={i === 0 ? "border-t-2 border-t-slate-200" : ""}>
              {i === 0 && (
                <td rowSpan={g.items.length} className="whitespace-nowrap bg-[rgb(238,242,250)]/40 px-4 py-3 align-top text-xs font-bold text-navy-700">
                  {g.title}
                </td>
              )}
              <td className="px-4 py-3 text-navy-700">
                <span className="inline-flex items-center gap-2">
                  <FileText className={`h-3.5 w-3.5 shrink-0 ${DOC_ICON_COLORS[i % DOC_ICON_COLORS.length]}`} />
                  {item}
                </span>
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

const ExtraBlock = ({ extra, accent }) => {
  if (!extra) return null;
  if (extra.kind === "table") {
    return (
      <div className="mt-6 overflow-hidden rounded-2xl border border-navy-900/10">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className={`${accent.bg} ${accent.text}`}>
                {extra.columns.map((c) => (
                  <th key={c} className="whitespace-nowrap px-4 py-3 font-bold">{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {extra.rows.map((row, i) => (
                <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                  {row.map((cell, j) => (
                    <td key={j} className="whitespace-normal px-4 py-3 text-slate-700">{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {extra.note && <p className="border-t border-navy-900/10 bg-slate-50 px-4 py-3 text-xs leading-relaxed text-slate-500">{extra.note}</p>}
      </div>
    );
  }
  if (extra.kind === "cards") {
    return (
      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
        {extra.cards.map((c) => (
          <div key={c.title} className={`rounded-2xl border ${accent.chip} p-5`}>
            <h4 className="text-sm font-bold text-navy-900">{c.title}</h4>
            <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{c.desc}</p>
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className={`mt-6 rounded-2xl border ${accent.chip} p-5`}>
      {extra.heading && <h4 className="text-sm font-bold text-navy-900">{extra.heading}</h4>}
      <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{extra.body}</p>
    </div>
  );
};

const ProcessStepper = ({ steps, accent }) => (
  <div className="relative mt-6">
    <div className="overflow-x-auto pb-1">
      <FlowDiagram steps={steps.map((s) => s.title)} accentSolidBg={accent.solidBg} accentText="text-white" />
    </div>
    <div className={`absolute left-4 top-20 bottom-4 hidden w-px bg-gradient-to-b ${accent.text.replace("text-", "from-")}/60 to-transparent lg:block xl:hidden`} aria-hidden="true" />
    <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-1 xl:grid-cols-2">
      {steps.map((step, i) => (
        <motion.div
          key={step.title}
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: false, margin: "-60px" }}
          transition={{ duration: 0.45, delay: (i % 2) * 0.08 }}
          className="relative flex gap-4 rounded-2xl border border-slate-200 bg-[rgb(238,242,250)]/50 p-5"
          data-testid={`service-step-${i}`}
        >
          <span className={`relative z-10 grid h-9 w-9 shrink-0 place-items-center rounded-full ${accent.solidBg} text-sm font-bold text-white`}>
            {String(i + 1).padStart(2, "0")}
          </span>
          <div>
            <h4 className="font-semibold text-navy-900">{step.title}</h4>
            <p className="mt-1 text-sm leading-relaxed text-slate-600">{step.desc}</p>
          </div>
        </motion.div>
      ))}
    </div>
  </div>
);

// Single heading + body block with an icon — used for standalone Timeline / Fees tabs.
const DetailCard = ({ icon: DIcon, heading, body, accent }) => (
  <div className={`flex items-start gap-4 rounded-2xl border ${accent.border} ${accent.chip} p-5 md:p-6`}>
    <motion.span
      initial={{ opacity: 0, scale: 0.8 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: false, margin: "-60px" }}
      transition={{ duration: 0.4 }}
      className={`grid h-12 w-12 shrink-0 place-items-center rounded-2xl ${accent.solidBg} text-white`}
    >
      <DIcon className="h-5 w-5" />
    </motion.span>
    <div>
      <h3 className={`text-sm font-bold uppercase tracking-wider ${accent.text}`}>{heading}</h3>
      <p className="mt-2 text-sm leading-relaxed text-slate-700">{body}</p>
    </div>
  </div>
);

const CardHeading = ({ children }) => <h2 className="text-lg font-bold text-navy-900">{children}</h2>;

// Horizontal, scroll-snap slider so ALL related services are reachable (not
// just the first 3) — arrow buttons nudge by roughly one card's width.
const RelatedServicesSlider = ({ items, accent }) => {
  const trackRef = useRef(null);
  const scrollByCard = (dir) => {
    const track = trackRef.current;
    if (!track) return;
    const card = track.querySelector("[data-related-card]");
    const amount = (card?.offsetWidth || 280) + 16;
    track.scrollBy({ left: dir * amount, behavior: "smooth" });
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <CardHeading>Related Services</CardHeading>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => scrollByCard(-1)}
            aria-label="Scroll related services left"
            data-testid="service-related-prev"
            className="grid h-9 w-9 place-items-center rounded-full border border-slate-200 text-navy-700 transition-colors hover:border-navy-300 hover:bg-[rgb(238,242,250)]"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => scrollByCard(1)}
            aria-label="Scroll related services right"
            data-testid="service-related-next"
            className="grid h-9 w-9 place-items-center rounded-full border border-slate-200 text-navy-700 transition-colors hover:border-navy-300 hover:bg-[rgb(238,242,250)]"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
      <div
        ref={trackRef}
        className="mt-4 flex snap-x snap-mandatory gap-4 overflow-x-auto pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        data-testid="service-related-track"
      >
        {items.map((r) => (
          <Link
            key={r.slug}
            to={r.href}
            data-related-card
            className="group flex w-[15.5rem] shrink-0 snap-start items-start gap-3 rounded-2xl border border-slate-200 bg-[rgb(238,242,250)]/50 p-4 transition-colors hover:border-navy-300"
          >
            <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-xl ${accent.bg} ${accent.text}`}>
              <Icon name={r.icon} className="h-4 w-4" />
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-semibold leading-snug text-navy-900 group-hover:text-navy-700">{r.title}</span>
              <span className="mt-1 flex items-center gap-1 text-xs font-semibold text-slate-500">
                View details <ArrowUpRight className="h-3 w-3" />
              </span>
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
};

// Bento-dashboard detail page for any "service" content (Registration, GST,
// Income Tax): every section is its own grid card of a deliberate size —
// nothing reads as one long column of stacked panels.
export default function ServiceDetailTemplate({
  service,
  accentKey = "gold",
  backHref,
  backLabel,
  breadcrumbs = [],
  contactHref,
  uploadHref,
  showUpload,
  relatedItems = [],
}) {
  const accent = ACCENT[accentKey] || ACCENT.gold;
  const activeSection = useScrollSpy(SERVICE_SECTIONS.map((s) => s.id));

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [service.slug]);

  const location = useLocation();
  const rawPath = location.pathname || (typeof window !== "undefined" ? window.location.pathname : `/services/${service.slug}`);
  const canonicalPath = rawPath.length > 1 && rawPath.endsWith("/") ? rawPath.slice(0, -1) : rawPath;
  const canonicalUrl = `https://www.biz360compliance.com${canonicalPath.startsWith('/') ? '' : '/'}${canonicalPath}`;

  const faqsList = Array.isArray(service.faqs)
    ? service.faqs
    : (Array.isArray(service.faqs?.items) ? service.faqs.items : []);

  const schemas = [
    getServiceSchema(service, canonicalUrl),
    getFAQPageSchema(faqsList)
  ].filter(Boolean);

  const fullBreadcrumbs = [
    ...breadcrumbs,
    ...(backHref && backLabel && !breadcrumbs.length ? [{ label: backLabel.replace(/^All\s+/, ''), href: backHref }] : []),
    { label: service.title, href: canonicalPath }
  ];

  return (
    <>
      <SEO
        title={`${service.title} | Biz360 Compliance`}
        description={`${service.subtitle ? service.subtitle + '. ' : ''}${service.intro ? service.intro.slice(0, 150) : ''}...`}
        canonical={canonicalPath}
        ogImage={service.image}
        schema={schemas}
        breadcrumbs={fullBreadcrumbs}
      />
      <section className="relative overflow-hidden bg-[rgb(238,242,250)] pt-32 pb-10 md:pt-40 md:pb-12">
        <AmbientOrbs />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
          {backHref ? (
            <Link to={backHref} className="inline-flex items-center gap-1.5 text-sm font-semibold text-navy-700 transition-colors hover:text-gold-600">
              <ArrowLeft className="h-4 w-4" /> {backLabel}
            </Link>
          ) : (
            <div className="flex flex-wrap items-center gap-1.5 text-xs font-medium text-slate-500" data-testid="service-breadcrumb">
              {breadcrumbs.map((b) => (
                <span key={b.href} className="flex items-center gap-1.5">
                  <Link to={b.href} className="hover:text-navy-800">{b.label}</Link>
                  <ChevronRight className="h-3 w-3" />
                </span>
              ))}
              <span className="text-navy-700">{service.title}</span>
            </div>
          )}
          <div className="mt-5 flex flex-wrap items-start gap-5">
            <span className={`grid h-14 w-14 shrink-0 place-items-center rounded-2xl ${accent.bg} ${accent.text}`}>
              <Icon name={service.icon} className="h-6 w-6" />
            </span>
            <div className="min-w-0 flex-1">
              <h1 className="text-2xl font-bold leading-tight tracking-tight text-navy-900 md:text-4xl">{service.title}</h1>
              <p className="mt-2 max-w-2xl text-base text-slate-600 md:text-lg">{service.subtitle}</p>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <span className={`inline-flex items-center gap-1.5 rounded-full border ${accent.border} ${accent.bg} px-3.5 py-1.5 text-xs font-bold uppercase tracking-wider ${accent.text}`}>
                  {service.category}
                </span>
                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500">
                  <Clock className="h-3.5 w-3.5" /> {service.readTime}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-5 py-12 lg:px-8 lg:py-16">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {/* Overview */}
          <Card span="lg:col-span-4" className="!p-0 overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-[2fr_3fr]">
              <img src={service.image} alt={service.title} className="h-56 w-full object-cover lg:h-full" loading="lazy" />
              <div className="p-6 md:p-8">
                <p className="text-[17px] leading-[1.8] text-slate-700">{service.intro}</p>
                <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
                  {[
                    { icon: Clock, label: "Est. turnaround", value: service.timeline.heading },
                    { icon: ListChecks, label: "Process", value: `${service.process.steps.length} steps` },
                    { icon: FileStack, label: "Documents", value: `${service.documents.groups.reduce((n, g) => n + g.items.length, 0)}+ required` },
                  ].map((stat, i) => (
                    <motion.div
                      key={stat.label}
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: false, margin: "-60px" }}
                      transition={{ duration: 0.35, delay: i * 0.08 }}
                      className={`flex items-center gap-2.5 rounded-xl border ${accent.chip} px-3.5 py-3`}
                    >
                      <stat.icon className={`h-4 w-4 shrink-0 ${accent.text}`} />
                      <div className="min-w-0">
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{stat.label}</p>
                        <p className="truncate text-sm font-semibold text-navy-900">{stat.value}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </Card>

          {/* CTA banner */}
          <Card span="lg:col-span-4" className={`${accent.chip} flex flex-col items-start justify-between gap-5 md:flex-row md:items-center`}>
            <div>
              <h3 className="text-lg font-bold text-navy-900">Ready to get started with {service.title}?</h3>
              <p className="mt-1.5 text-sm text-slate-600">Talk to our team, or send us your documents to begin.</p>
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-3.5">
              {/* Button 1: Book Free Consultation (Golden/amber pill, dark navy text & icons) */}
              <Link
                to={contactHref}
                data-testid="sidebar-cta-contact"
                className="group relative inline-flex items-center gap-2.5 whitespace-nowrap rounded-full bg-[#f2a40b] px-4 py-2.5 text-xs sm:px-5 sm:py-3 sm:text-sm font-bold text-[#08254a] shadow-[0_8px_22px_rgba(242,164,11,0.35)] ring-1 ring-amber-400/40 transition-all duration-300 hover:scale-[1.02] hover:shadow-[0_12px_28px_rgba(242,164,11,0.5)] active:scale-[0.98]"
              >
                {/* Left circular icon badge */}
                <span className="grid h-7 w-7 sm:h-8 sm:w-8 shrink-0 place-items-center rounded-full bg-black/10 transition-transform duration-300 group-hover:rotate-12">
                  <Sparkles className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-[#08254a]" />
                </span>

                <span className="font-bold tracking-tight">Book Free Consultation</span>

                {/* Right circular icon badge */}
                <span className="grid h-7 w-7 sm:h-8 sm:w-8 shrink-0 place-items-center rounded-full bg-black/10 transition-transform duration-300 group-hover:translate-x-0.5">
                  <ArrowRight className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-[#08254a]" />
                </span>
              </Link>

              {/* Button 2: Upload Documents (Royal blue pill, white text & cloud, white status dot) */}
              {showUpload && (
                <Link
                  to={uploadHref}
                  data-testid="sidebar-cta-upload"
                  className="group relative inline-flex items-center gap-2.5 whitespace-nowrap rounded-full bg-[#1970f6] px-4 py-2.5 text-xs sm:px-5 sm:py-3 sm:text-sm font-bold text-white shadow-[0_8px_22px_rgba(25,112,246,0.35)] ring-1 ring-blue-400/40 transition-all duration-300 hover:scale-[1.02] hover:shadow-[0_12px_28px_rgba(25,112,246,0.5)] active:scale-[0.98]"
                >
                  {/* Left circular icon badge */}
                  <span className="grid h-7 w-7 sm:h-8 sm:w-8 shrink-0 place-items-center rounded-full bg-white/20 transition-transform duration-300 group-hover:-translate-y-0.5">
                    <UploadCloud className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-white" />
                  </span>

                  <span className="font-bold tracking-tight">Upload Documents</span>

                  {/* Right white indicator dot */}
                  <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-white shadow-sm" />
                </Link>
              )}

              {/* Button 3: Telephone CTA (White pill, solid green badge, dark navy text, green status dot) */}
              <a
                href={`tel:${PHONE.replace(/\s+/g, "")}`}
                data-testid="sidebar-cta-phone"
                className="group inline-flex items-center gap-2.5 whitespace-nowrap rounded-full border border-slate-200/90 bg-white px-4 py-2.5 text-xs sm:px-5 sm:py-3 sm:text-sm font-bold text-[#0b2a54] shadow-[0_4px_16px_rgba(0,0,0,0.06)] transition-all duration-300 hover:border-emerald-500/40 hover:scale-[1.02] hover:shadow-[0_8px_22px_rgba(22,122,61,0.15)] active:scale-[0.98]"
              >
                <span className="grid h-7 w-7 sm:h-8 sm:w-8 shrink-0 place-items-center rounded-full bg-[#167a3d] text-white shadow-sm transition-transform duration-300 group-hover:rotate-12">
                  <Phone className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-white fill-white/20" />
                </span>
                <span className="font-bold tracking-tight text-[#0b2a54]">{PHONE}</span>
                <span className="relative flex h-2.5 w-2.5 shrink-0 items-center justify-center">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#21be89] opacity-75" />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#21be89]" />
                </span>
              </a>
            </div>
          </Card>

          {/* Everything below used to be one long stack of cards — tabbed so
              visitors reach any section (documents, fees, FAQs...) in one click. */}
          <Card span="lg:col-span-4" className="!p-0">
            <div
              className="sticky z-30 flex h-auto w-full flex-wrap justify-start gap-1.5 rounded-t-3xl border-b border-slate-200 bg-white/95 p-4 pb-3 backdrop-blur md:px-8"
              style={{ top: NAV_OFFSET }}
              data-testid="service-tabs-bar"
            >
              {SERVICE_SECTIONS.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => scrollToSection(s.id)}
                  data-testid={`service-tab-${s.id}`}
                  className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${
                    activeSection === s.id
                      ? "border-transparent bg-navy-900 text-white"
                      : "border-slate-200 text-navy-700 hover:border-navy-300"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>

            <section id="eligibility" className="p-6 md:p-8">
              <CardHeading>{service.eligibility.heading}</CardHeading>
              <InfoList items={service.eligibility.items} icons={[ShieldCheck, BadgeCheck, UserCheck, FileCheck]} />
              <div className="mt-8">
                <CardHeading>{service.whoNeedsIt.heading}</CardHeading>
                <InfoList items={service.whoNeedsIt.items} icons={[Users, Building2, User, Globe]} />
              </div>
            </section>

            <section id="documents" className="border-t border-slate-100 p-6 md:p-8">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <CardHeading>{service.documents.heading}</CardHeading>
                {showUpload && (
                  <Link
                    to={uploadHref}
                    data-testid="documents-cta-upload"
                    className="group relative inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-[#1970f6] px-4 py-2 text-xs font-bold text-white shadow-[0_6px_20px_rgba(25,112,246,0.35)] ring-1 ring-blue-400/40 transition-all duration-300 hover:scale-[1.02] hover:shadow-[0_10px_24px_rgba(25,112,246,0.5)] active:scale-[0.98]"
                  >
                    <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-white/20 transition-transform duration-300 group-hover:-translate-y-0.5">
                      <UploadCloud className="h-3.5 w-3.5 text-white" />
                    </span>
                    <span className="font-bold tracking-tight">Upload Documents</span>
                    <span className="h-2 w-2 shrink-0 rounded-full bg-white shadow-sm" />
                  </Link>
                )}
              </div>
              <DocumentGroups groups={service.documents.groups} />
              <ExtraBlock extra={service.extra} accent={accent} />
            </section>

            <section id="process" className="border-t border-slate-100 p-6 md:p-8">
              <CardHeading>{service.process.heading}</CardHeading>
              <ProcessStepper steps={service.process.steps} accent={accent} />
            </section>

            <section id="timeline" className="border-t border-slate-100 p-6 md:p-8">
              <CardHeading>Estimated Timeline</CardHeading>
              <div className="mt-4">
                <DetailCard icon={Calendar} heading={service.timeline.heading} body={service.timeline.body} accent={accent} />
              </div>
            </section>

            <section id="fees" className="border-t border-slate-100 p-6 md:p-8">
              <CardHeading>Professional Fees</CardHeading>
              <div className="mt-4">
                <DetailCard icon={IndianRupee} heading={service.fees.heading} body={service.fees.note} accent={accent} />
              </div>
            </section>

            <section id="benefits" className="border-t border-slate-100 p-6 md:p-8">
              <CardHeading>{service.benefits.heading}</CardHeading>
              <InfoList items={service.benefits.items} icons={[Award, TrendingUp, Zap, HeartHandshake, Sparkles]} />
              {service.afterService && (
                <div className="mt-8">
                  <CardHeading>{service.afterService.heading}</CardHeading>
                  <InfoList items={service.afterService.items} icons={[RefreshCw, Calendar, BellRing, ClipboardList]} />
                </div>
              )}
            </section>

            <section id="faqs" className="border-t border-slate-100 p-6 md:p-8">
              <CardHeading>Frequently Asked Questions</CardHeading>
              <Accordion type="single" collapsible className="mt-4 divide-y divide-navy-900/10" data-testid="service-faq-accordion">
                {service.faqs.map((item, i) => (
                  <AccordionItem key={item.q} value={`sfaq-${i}`} className="border-b border-navy-900/10 py-1.5">
                    <AccordionTrigger className="text-left text-base font-semibold text-navy-900 hover:no-underline">{item.q}</AccordionTrigger>
                    <AccordionContent className="text-sm leading-relaxed text-slate-600">{item.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </section>
          </Card>

          {/* Why choose us */}
          <Card span="lg:col-span-4" className="!border-0 !bg-navy-900">
            <h3 className="text-lg font-semibold text-white">{service.whyUs.heading}</h3>
            <ul className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {service.whyUs.items.map((item, i) => (
                <motion.li
                  key={item}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: false, margin: "-60px" }}
                  transition={{ duration: 0.35, delay: (i % 6) * 0.06 }}
                  className="flex items-start gap-2.5 text-sm text-white/75"
                >
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold-400" />
                  {item}
                </motion.li>
              ))}
            </ul>
          </Card>

          {/* Related services — horizontal slider so every related service is reachable, not just the first few */}
          {relatedItems.length > 0 && (
            <Card span="lg:col-span-4" data-testid="service-related">
              <RelatedServicesSlider items={relatedItems} accent={accent} />
            </Card>
          )}
        </div>
      </div>
    </>
  );
}
